<script setup lang="ts">
/**
 * CDB54 Calendrier+ - Install Prompt Component
 * Story 4.1: PWA install prompt
 */

import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePwa } from '@/composables/usePwa';

const { t } = useI18n();
const { canInstall, installApp } = usePwa();
const dismissed = ref(false);

async function handleInstall(): Promise<void> {
  await installApp();
  dismissed.value = true;
}

function dismiss(): void {
  dismissed.value = true;
  // Store dismissal in localStorage
  localStorage.setItem('pwa-install-dismissed', 'true');
}

// Check if previously dismissed
const wasDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
if (wasDismissed) {
  dismissed.value = true;
}
</script>

<template>
  <Transition name="fade">
    <div v-if="canInstall && !dismissed" class="install-prompt card shadow-lg">
      <div class="card-body">
        <div class="d-flex align-items-start">
          <i class="bi bi-download fs-3 text-primary me-3"></i>
          <div class="flex-grow-1">
            <h6 class="card-title mb-1">{{ t('pwa.install_title') }}</h6>
            <p class="card-text text-muted small mb-2">
              {{ t('pwa.install_description') }}
            </p>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary" @click="dismiss">
                {{ t('pwa.not_now') }}
              </button>
              <button class="btn btn-sm btn-primary" @click="handleInstall">
                {{ t('pwa.install') }}
              </button>
            </div>
          </div>
          <button
            type="button"
            class="btn-close"
            :aria-label="t('common.close')"
            @click="dismiss"
          ></button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.install-prompt {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  max-width: 400px;
  z-index: 1055;
}

@media (min-width: 576px) {
  .install-prompt {
    left: auto;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
